import { createCsvBlob, repairCsvText } from './csvEncoding'

const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return ''
  if (typeof val === 'number') return val.toString()
  if (typeof val === 'object') return JSON.stringify(val)

  const v = String(val).trim()

if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
  try {
    const d = new Date(v)

    if (Number.isNaN(d.getTime())) return v

    const day = String(d.getDate()).padStart(2,'0')
    const month = String(d.getMonth()+1).padStart(2,'0')
    const year = d.getFullYear()

    return `${day}.${month}.${year}`
  } catch {
    return v
  }
}

  return v
}

const pad = (n) => String(n).padStart(2, '0')

const fileStamp = () => {
  const d = new Date()
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

const downloadBlob = (blob, name) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

const csvEscape = (value) => `"${repairCsvText(formatValue(value))}"`

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)

  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i])
  }

  return window.btoa(binary)
}

const loadPdfFont = async (doc, url, fileName, fontName, fontStyle) => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`PDF font not found: ${url}`)
  }

  const buffer = await response.arrayBuffer()
  const base64 = arrayBufferToBase64(buffer)

  doc.addFileToVFS(fileName, base64)
  doc.addFont(fileName, fontName, fontStyle)
}

const ensureServicePdfFonts = async (doc) => {
  await loadPdfFont(
    doc,
    '/fonts/Roboto-Regular.ttf',
    'Roboto-Regular.ttf',
    'Roboto',
    'normal'
  )

  await loadPdfFont(
    doc,
    '/fonts/Roboto-Bold.ttf',
    'Roboto-Bold.ttf',
    'Roboto',
    'bold'
  )
}

const translateServiceHeader = (h, t) => {

  const map = {
    date: 'TRIPLOG.SERVICECOSTS.TABLE.date',
    vehicleId: 'TRIPLOG.SERVICECOSTS.TABLE.vehicleId',
    serviceType: 'TRIPLOG.SERVICECOSTS.TABLE.serviceType',
    kilometers: 'TRIPLOG.SERVICECOSTS.TABLE.kilometers',
    description: 'TRIPLOG.SERVICECOSTS.TABLE.description',
    laborCost: 'TRIPLOG.SERVICECOSTS.TABLE.laborCost',
    materialCost: 'TRIPLOG.SERVICECOSTS.TABLE.materialCost',
    totalCost: 'TRIPLOG.SERVICECOSTS.TABLE.totalCost',
    workshopName: 'TRIPLOG.SERVICECOSTS.TABLE.workshopName',
    workshopAddress: 'TRIPLOG.SERVICECOSTS.TABLE.workshopAddress',
    documentNumber: 'TRIPLOG.SERVICECOSTS.TABLE.documentNumber',
    note: 'TRIPLOG.SERVICECOSTS.TABLE.note'
  }

  if (h === 'spz') return t('vehicle_license_plate')

  const key = map[h]

  if (key) return t(key)

  return h
}

export const buildServiceExportRecords = (services, vehiclesById) => {
  return services.map((s) => {

    const vehicle = vehiclesById[String(s?.vehicleId)] || s?.vehicle

    const vehicleName =
      vehicle?.name ||
      vehicle?.model ||
      vehicle?.licensePlate ||
      vehicle?.spz ||
      ''

    const plate = vehicle?.licensePlate || vehicle?.spz || ''

    return {
      date: s?.date,
      vehicleId: vehicleName,
      spz: plate,
      serviceType: s?.serviceType ?? '',
      kilometers: s?.kilometers ?? '',
      description: s?.description ?? '',
      laborCost: s?.laborCost ?? '',
      materialCost: s?.materialCost ?? '',
      totalCost: s?.totalCost ?? '',
      workshopName: s?.workshopName ?? '',
      workshopAddress: s?.workshopAddress ?? '',
      documentNumber: s?.documentNumber ?? '',
      note: s?.note ?? ''
    }
  })
}

export const exportServicesCSV = (records, headers, t, periodText, vehicleText) => {

  const titleRows = [
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD')};"${repairCsvText(periodText)}"`,
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE')};"${repairCsvText(vehicleText)}"`,
    ''
  ]

  const header = headers.map(h => translateServiceHeader(h, t)).join(';')

  const rows = records.map(r =>
    headers.map(h => csvEscape(r[h])).join(';')
  )

  const csv = [
    ...titleRows,
    header,
    ...rows
  ].join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `services_${fileStamp()}.csv`
  )
}

export const exportServicesXLS = async (records, headers, t, periodText, vehicleText) => {

  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [t('TRIPLOG.STATISTICS.SHOWN_PERIOD'), periodText],
    [t('TRIPLOG.STATISTICS.SHOWN_VEHICLE'), vehicleText],
    []
  ]

  const translatedHeaders = headers.map(h => translateServiceHeader(h, t))
  const dataRows = records.map(r => headers.map(h => formatValue(r[h]) ?? ''))

  const worksheetData = [...titleRows, translatedHeaders, ...dataRows]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  ws['!cols'] = headers.map(() => ({ wch: 18 }))

  const headerRowIndex = titleRows.length

  translatedHeaders.forEach((_, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: colIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: { rgb: 'F0F0F0' }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  titleRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  dataRows.forEach((row, rowIndex) => {
    row.forEach((_, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: colIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Services')

  XLSX.writeFile(wb, `services_${fileStamp()}.xlsx`)
}

export const exportServicesPDF = async (records, headers, t, periodText, vehicleText) => {

  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')
  const autoTable = autoTableModule.default || autoTableModule.autoTable

  const translatedHeaders = headers.map(h => translateServiceHeader(h, t))
  const dataRows = records.map(r => headers.map(h => formatValue(r[h]) ?? ''))

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureServicePdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18
  const pageWidth = doc.internal.pageSize.getWidth()
  const usableWidth = pageWidth - marginLeft - marginRight
  const columnWidth = usableWidth / headers.length

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)
  doc.text(t('TRIPLOG.TAB.SERVIS'), marginLeft, 28)

  doc.setFontSize(10)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD')}: ${new Date().getFullYear()}`,
    marginLeft,
    44
  )

  doc.setFontSize(9)
  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', { defaultValue: 'Zobrazené vozidlo' })}: ${vehicleText}`,
    marginLeft,
    58
  )

  autoTable(doc, {
    head: [translatedHeaders],
    body: dataRows,
    startY: 72,
    theme: 'grid',
    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },
    tableWidth: usableWidth,
    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 6,
      cellPadding: 2,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 10
    },
    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 6,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },
    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },
    columnStyles: headers.reduce((acc, _header, index) => {
      acc[index] = {
        cellWidth: columnWidth
      }

      return acc
    }, {}),
    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(doc.output('blob'), `services_${fileStamp()}.pdf`)
}

export const exportServicesXML = (records, headers) => {

  const rows = records
    .map(r => {
      const fields = headers
        .map(h => `<${h}>${formatValue(r[h])}</${h}>`)
        .join('')
      return `<service>${fields}</service>`
    })
    .join('')

  const xml = `<?xml version="1.0" encoding="UTF-8"?><services>${rows}</services>`

  downloadBlob(
    new Blob([xml], { type: 'application/xml;charset=utf-8;' }),
    `services_${fileStamp()}.xml`
  )
}

export const exportServicesJSON = (records) => {

  downloadBlob(
    new Blob([JSON.stringify(records, null, 2)], {
      type: 'application/json;charset=utf-8;'
    }),
    `services_${fileStamp()}.json`
  )
}